<template lang="pug">
.api-tokens
    h4 {{ $t('API tokens') }}
    p.description {{ description }}

    .token-list(v-if="tokens.length")
        .token-row(v-for="token in tokens" :key="token.id")
            .token-info
                strong {{ token.name }}
                .meta
                    span {{ $t('Created') }}: {{ formatDate(token.created_at) }}
                    span(v-if="token.last_used_at")  · {{ $t('Last used') }}: {{ formatDate(token.last_used_at) }}
                .created-token(v-if="newlyCreatedTokenId === token.id")
                    .token-warning
                        i.el-icon-warning
                        |  {{ $t('Copy this token now. It will not be shown again.') }}
                    .token-display
                        code {{ newlyCreatedToken }}
                        el-button(size="mini" type="text" @click="copyNewToken")
                            i(:class="copiedNewToken ? 'el-icon-check' : 'el-icon-document-copy'")
            el-button(
                circle
                type="danger"
                size="mini"
                icon="el-icon-fa-trash-alt far"
                @click="$refs.deleteDialog.open(token)"
            )

    .no-tokens(v-else-if="!loading")
        p {{ $t('No API tokens created yet.') }}

    .actions
        el-button(type="primary" size="small" icon="el-icon-fa-plus fas" @click="showCreateDialog = true") {{ $t('Create Token') }}

    confirm-delete-dialog(
        ref="deleteDialog"
        :title="$t('Delete Token')"
        :message="$t('Are you sure you want to delete this token? Any systems using it will lose access.')"
        @confirm="deleteToken"
    )

    el-dialog(
        :title="$t('Create API Token')"
        :visible.sync="showCreateDialog"
        width="400px"
        :close-on-click-modal="false"
    )
        el-form(@submit.native.prevent="createToken")
            el-form-item.no-margin(:label="$t('Token Name')")
                el-input(v-model="newTokenName" autofocus)
        template(#footer)
            el-button(@click="showCreateDialog = false") {{ $t('Cancel') }}
            el-button(type="primary" @click="createToken" :loading="creating") {{ $t('Create') }}
</template>

<i18n>
{
    "de": {
        "API tokens": "API-Token",
        "No API tokens created yet.": "Noch keine API-Tokens erstellt.",
        "Copy this token now. It will not be shown again.": "Kopieren Sie diesen Token jetzt. Er wird nicht erneut angezeigt.",
        "Create Token": "Token erstellen",
        "Create API Token": "API-Token erstellen",
        "Token Name": "Token-Name",
        "Created": "Erstellt",
        "Last used": "Zuletzt verwendet",
        "Create": "Erstellen",
        "Cancel": "Abbrechen",
        "Delete Token": "Token löschen",
        "Are you sure you want to delete this token? Any systems using it will lose access.": "Sind Sie sicher, dass Sie diesen Token löschen möchten? Alle Systeme, die ihn verwenden, verlieren den Zugriff.",
        "An error occurred!": "Ein Fehler ist aufgetreten!"
    }
}
</i18n>

<script>
import { timestamp2date } from '@/filters'
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog'

/*
 * One token list for every tokenable: the parent injects where the tokens come
 * from (member integration tokens, the own user's personal tokens) and decides
 * when to render it (a persisted id). The component only lists, mints and revokes.
 */
export default {
    name: 'ApiTokens',
    components: {
        ConfirmDeleteDialog
    },
    props: {
        description: { type: String, required: true },
        fetch: { type: Function, required: true },
        create: { type: Function, required: true },
        remove: { type: Function, required: true }
    },
    data() {
        return {
            loading: false,
            creating: false,
            tokens: [],
            showCreateDialog: false,
            newTokenName: '',
            newlyCreatedToken: null,
            newlyCreatedTokenId: null,
            copiedNewToken: false
        }
    },
    mounted() {
        this.loadTokens()
    },
    methods: {
        formatDate(timestamp) {
            if (!timestamp) return '—'
            return timestamp2date(timestamp, this.$t('yyyy-MM-dd') + ', HH:mm')
        },
        loadTokens() {
            this.loading = true
            this.fetch()
                .then(tokens => {
                    this.tokens = tokens || []
                })
                .catch(error => this.notifyError(error))
                .finally(() => {
                    this.loading = false
                })
        },
        createToken() {
            const name = this.newTokenName.trim()
            if (!name) return

            this.creating = true
            this.create(name)
                .then(token => {
                    this.newlyCreatedToken = token.plainTextToken
                    this.newlyCreatedTokenId = token.id
                    this.showCreateDialog = false
                    this.newTokenName = ''
                    this.loadTokens()
                })
                .catch(error => this.notifyError(error))
                .finally(() => {
                    this.creating = false
                })
        },
        deleteToken(token) {
            return this.remove(token.id)
                .then(() => {
                    if (this.newlyCreatedTokenId === token.id) {
                        this.newlyCreatedToken = null
                        this.newlyCreatedTokenId = null
                    }
                    this.loadTokens()
                })
                .catch(error => this.notifyError(error))
        },
        copyNewToken() {
            navigator.clipboard.writeText(this.newlyCreatedToken)
            this.copiedNewToken = true
            setTimeout(() => {
                this.copiedNewToken = false
            }, 2000)
        },
        notifyError(error) {
            console.error(error)
            this.$notify.error({
                title: this.$t('An error occurred!'),
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            })
        }
    }
}
</script>

<style lang="sass" scoped>
.api-tokens
    margin-top: 1.5rem
    margin-bottom: 1.5rem

    h4
        margin: 0 0 4px
        font-size: 1rem
        font-weight: 600

    .description
        color: #909399
        font-size: 0.875rem
        line-height: 1.4
        margin: 0 0 16px

    .token-list
        margin-bottom: 12px

    .token-row
        display: flex
        align-items: center
        justify-content: space-between
        padding: 10px 12px
        background: #f9fafb
        border: 1px solid #ebeef5
        border-radius: 4px
        margin-bottom: 6px

        .token-info
            flex: 1
            min-width: 0

            strong
                font-size: 0.875rem

            .meta
                font-size: 0.75rem
                color: #909399
                margin-top: 2px

        .created-token
            margin-top: 8px

            .token-warning
                color: #e6a23c
                font-size: 0.8125rem
                margin-bottom: 6px

                .el-icon-warning
                    margin-right: 4px

            .token-display
                display: flex
                align-items: center
                gap: 6px

                code
                    flex: 1
                    padding: 6px 8px
                    background: #fdf6ec
                    border: 1px solid #f5dab1
                    border-radius: 3px
                    font-size: 0.75rem
                    word-break: break-all
                    user-select: all

    .no-tokens
        p
            color: #909399
            font-size: 0.875rem
            margin-bottom: 12px

    .actions
        margin-top: 4px
</style>
